/**
 * useDraftAutosave — shared autosave hook for the Universal Editable
 * Preview Mode (Phase 1: Ebook).
 *
 * Snapshots the current value to localStorage every `intervalMs`
 * (default: 10s) AND on every change with a 500 ms debounce, so the user
 * never loses more than 500 ms of typing if they hard-refresh.
 *
 * IMPORTANT — does NOT call any backend endpoint, does NOT touch
 * Business Memory, does NOT touch the canonical asset row in Mongo.
 * The ONLY persistence it does is in window.localStorage, keyed by the
 * caller-supplied `storageKey`. The persistent row in Mongo is only
 * touched when the user clicks "Save Final" → PATCH /api/{builder}/{id}.
 *
 * Usage:
 *   const { restored, clear } = useDraftAutosave(`ebook-draft-${id}`, draft, !!draft);
 *
 * Return value:
 *   restored: the most recent draft found in localStorage on mount
 *             (or null if none) — caller decides whether to apply it.
 *   clear():  delete the draft from localStorage. Call this after the
 *             "Save Final" round-trip succeeds.
 */
import { useEffect, useRef, useState } from 'react';

const DEFAULT_INTERVAL_MS = 10_000;
const DEBOUNCE_MS = 500;

export default function useDraftAutosave(storageKey, value, enabled = true, intervalMs = DEFAULT_INTERVAL_MS) {
  const [restored, setRestored] = useState(null);
  const lastWrittenRef = useRef('');

  // On mount: pull any existing draft so the caller can offer to restore.
  useEffect(() => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) setRestored(JSON.parse(raw));
    } catch {
      /* corrupt entry — ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  // Debounced + interval autosave.
  useEffect(() => {
    if (!enabled || !storageKey || typeof window === 'undefined') return undefined;

    const write = () => {
      try {
        const serialized = JSON.stringify({ value, savedAt: Date.now() });
        if (serialized === lastWrittenRef.current) return;
        window.localStorage.setItem(storageKey, serialized);
        lastWrittenRef.current = serialized;
      } catch {
        /* quota / privacy mode — silently drop */
      }
    };

    const debounceTimer = window.setTimeout(write, DEBOUNCE_MS);
    const intervalTimer = window.setInterval(write, intervalMs);

    return () => {
      window.clearTimeout(debounceTimer);
      window.clearInterval(intervalTimer);
    };
  }, [storageKey, value, enabled, intervalMs]);

  const clear = () => {
    if (!storageKey || typeof window === 'undefined') return;
    try {
      window.localStorage.removeItem(storageKey);
      lastWrittenRef.current = '';
      setRestored(null);
    } catch { /* ignore */ }
  };

  return { restored, clear };
}
